/**
 * OrgSwitcher — sidebar chip showing the active studio.
 *
 * Sits ABOVE the user-profile link in the sidebar. Clicking opens
 * `OrgSwitcherDialog`. Visually:
 *
 *   ┌──────────────────────────────┐
 *   │ [logo]  Studio Name        ⇅ │
 *   │         Switch studio         │
 *   └──────────────────────────────┘
 *
 * The logo falls back to a colored initial when `org.logoUrl` is
 * unset. Tap target is the entire chip — easy to discover and miss-
 * proof. Stays mounted even while the dialog is open so the
 * background doesn't shift.
 *
 * Mirrors mobile's `src/ui/v2/OrgSwitcher.tsx` chip pattern (compact,
 * single row with chevron) but tuned for the wider web sidebar.
 */
import { useState } from 'react';

import { useFullOrganization } from '@/lib/useFullOrganization';

import { OrgSwitcherDialog } from '@/components/OrgSwitcherDialog';

export function OrgSwitcher() {
  const { org } = useFullOrganization();
  const [open, setOpen] = useState(false);

  const orgData = (org?.data ?? {}) as { name?: string; logoUrl?: string };
  const studioName = orgData.name?.trim() || 'Your Studio';
  const logoUrl = orgData.logoUrl?.trim() || null;
  const accent = orgAccentColor(studioName);
  const initials = orgShortName(studioName);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Switch studio"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          padding: '8px 10px',
          marginBottom: 8,
          background: 'transparent',
          border: '1px solid var(--border, rgba(0,0,0,0.08))',
          borderRadius: 10,
          cursor: 'pointer',
          font: 'inherit',
          color: 'inherit',
          textAlign: 'left',
          transition: 'background 0.12s ease, border-color 0.12s ease',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background =
            'rgba(59,130,246,0.06)';
          (e.currentTarget as HTMLButtonElement).style.borderColor =
            'rgba(59,130,246,0.2)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
          (e.currentTarget as HTMLButtonElement).style.borderColor =
            'var(--border, rgba(0,0,0,0.08))';
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: logoUrl ? 'transparent' : accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 0.4,
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            initials
          )}
        </div>
        <div
          className="text"
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {studioName}
          </div>
          <div
            style={{
              fontSize: 10,
              color: 'var(--text-secondary)',
              letterSpacing: 0.3,
            }}
          >
            Switch studio
          </div>
        </div>
        <i
          className="ph ph-caret-up-down"
          aria-hidden
          style={{
            color: 'var(--text-secondary)',
            fontSize: 14,
            flexShrink: 0,
          }}
        />
      </button>

      <OrgSwitcherDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

// ───── Avatar helpers (must match OrgSwitcherDialog) ─────────

const ACCENT_PALETTE = [
  '#0EA5E9',
  '#22C55E',
  '#F97316',
  '#A855F7',
  '#E11D48',
  '#0891B2',
  '#84CC16',
  '#EA580C',
  '#7C3AED',
  '#DB2777',
];

function orgAccentColor(name: string): string {
  if (!name) return ACCENT_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return ACCENT_PALETTE[hash % ACCENT_PALETTE.length];
}

function orgShortName(name: string): string {
  if (!name) return '·';
  const words = name.trim().split(/\s+/).slice(0, 2);
  return words.map((w) => w.charAt(0).toUpperCase()).join('');
}
